import {
  WorkOrderType,
  WorkOrderStatus,
  getCountryAbbreviationFromWorkOrderType,
  EuCountryCodes,
} from '@xbcb/work-order-types';
import { AppRecordKebabMenuItem } from 'routes';
import { pascalCase } from 'change-case';
import { getRecordType } from '@xbcb/core';
import { RecordType } from '@xbcb/shared-types';
import { AppRecordOnConfirmPayload } from '../../types';
import { safeGetMessage, setModal } from '@xbcb/ui-utils';
import { ModalKey } from '@xbcb/ui-types';
import {
  updateEuCustomsEntry,
  updateUsConsumptionEntry,
} from 'libs/sharedQueries';

import { checkAccess, UserPermissions } from '@xbcb/client-utils/dist';
import { WorkOrderBadge } from '@xbcb/work-order-types/dist/enums/workOrder/badge';
import { isRecordCanceled, isRecordDeleted } from 'libs/workOrderConditions';
import { fetchLatestRecordAndExecuteMutation } from 'libs/fetchLatestRecordAndExecuteMutation';
import { updateCustomsDeclaration } from '../workOrderRoutes/customsDeclaration/customsDeclaration.query';
import { DocumentNode } from 'graphql';
import {
  euClassificationBadges,
  usClassificationBadges,
  US_BADGE_SUPPORTED_RECORD_TYPES,
} from 'constants/analysisBadgesConfig';

const getCurrentClassificationBadges = (
  workOrder: { badges?: WorkOrderBadge[] },
  badges: Array<{ badge: WorkOrderBadge }>,
): WorkOrderBadge[] => {
  const currentBadges = workOrder?.badges || [];
  const classificationBadgeTypes = badges.map((c) => c.badge);
  return currentBadges.filter((badge: WorkOrderBadge) =>
    classificationBadgeTypes.includes(badge),
  );
};

const getMutationForRecordType = (recordType: RecordType): DocumentNode => {
  if (US_BADGE_SUPPORTED_RECORD_TYPES.includes(recordType)) {
    return updateUsConsumptionEntry;
  }
  if (recordType === RecordType.CUSTOMS_DECLARATION) {
    return updateCustomsDeclaration;
  }
  return updateEuCustomsEntry(
    getCountryAbbreviationFromWorkOrderType(
      pascalCase(recordType) as WorkOrderType,
    ) as EuCountryCodes,
  );
};

const getClassificationBadgesForRecordType = (
  recordType: RecordType,
): Array<{ badge: WorkOrderBadge }> => {
  if (US_BADGE_SUPPORTED_RECORD_TYPES.includes(recordType)) {
    return usClassificationBadges;
  }
  return euClassificationBadges;
};

export const createAnalysisBadgesMenuItem = (
  overrideBadges?: Array<{ badge: WorkOrderBadge }>,
): AppRecordKebabMenuItem => ({
  key: 'manageAnalysisBadges',
  text: ({ appRecordBundle }) =>
    safeGetMessage(appRecordBundle, 'manage_analysis_badges'),
  show: ({ existingRecord, currentUser }) => {
    if (!existingRecord?.id || !existingRecord?.status) {
      return false;
    }
    const recordType = getRecordType(existingRecord.id) as RecordType;
    return (
      ![
        WorkOrderStatus.NOT_STARTED,
        WorkOrderStatus.COMPLETED,
        WorkOrderStatus.REPLACED,
      ].includes(existingRecord.status) &&
      !isRecordCanceled(existingRecord) &&
      !isRecordDeleted(existingRecord) &&
      checkAccess(currentUser, recordType, UserPermissions.UPDATE)
    );
  },
  action: ({ dispatch, record }) => {
    const existingRecord = record;
    const recordType = getRecordType(existingRecord.id) as RecordType;
    const classificationBadges =
      overrideBadges || getClassificationBadgesForRecordType(recordType);
    dispatch(
      setModal({
        key: ModalKey.EDIT_WORK_ORDER_ANALYSIS_BADGES,
        props: {
          visible: true,
          sendConfirmationDecision: async (
            payload: AppRecordOnConfirmPayload & {
              selectedBadges?: WorkOrderBadge[];
            },
          ) => {
            if (payload.canceled) {
              return;
            }

            const workOrder = existingRecord;
            const selectedBadges = payload.selectedBadges || [];
            const currentBadges = workOrder.badges || [];
            const classificationBadgeTypes = classificationBadges.map(
              (c) => c.badge,
            );
            const nonClassificationBadges = currentBadges.filter(
              (badge: WorkOrderBadge) =>
                !classificationBadgeTypes.includes(badge),
            );
            const updatedBadges = [
              ...nonClassificationBadges,
              ...selectedBadges,
            ];

            await fetchLatestRecordAndExecuteMutation({
              id: workOrder.id,
              recordType,
              mutation: getMutationForRecordType(recordType),
              constructVariablesWithIdVersion: (id, version) => ({
                id: id,
                version: version,
                input: {
                  badges: updatedBadges,
                },
              }),
            });
          },
          workOrders: [existingRecord],
          workOrderType: recordType,
          availableClassifications: classificationBadges,
          currentClassifications: getCurrentClassificationBadges(
            existingRecord,
            classificationBadges,
          ),
        },
      }),
    );
  },
});

export const analysisBadgesMenuItem: AppRecordKebabMenuItem =
  createAnalysisBadgesMenuItem();
